// Catálogo de VITRINA — módulo core `products` de lealify-base (multitenant, /api/products).
// Mismo contrato que la agenda dental (agenda-compat.js): GET view · POST/PUT/DELETE manage.
import { api } from './apiClient';

export interface Product {
  id: string;
  name: string;
  brand?: string;
  category?: string;
  price: number;
  cost?: number;
  sku?: string;
  /** Dato INTERNO (ubicación, lote…). No se enseña al vender. */
  notes?: string;
  /** Descripción opcional: lo que se le dice al cliente. Se enseña al vender. */
  description?: string;
  stock?: number;
  /** Semáforo (29-sep-2026): rojo ≤ stockMin · ámbar ≤ stockMid · stockMax = tope de compra (no es color). */
  stockMin?: number;
  stockMid?: number;
  stockMax?: number;
  isActive: boolean;
  /** Foto del producto (servida con sesión por /api/avatars/product/:id; ?v= cambia con la foto). */
  photoUrl?: string;
}

export type NivelStock = 'rojo' | 'ambar' | 'verde' | null;
/** null = gris: sin control de existencias o sin umbrales (NULL o 0/0). Misma regla que el backend
 *  (lib/vitrinaStock.js → nivel): si cambia una, cambia la otra. */
export function nivelStock(p: { stock?: number | null; stockMin?: number | null; stockMid?: number | null }): NivelStock {
  if (p.stock == null) return null;
  const a = p.stockMin ?? 0, b = p.stockMid ?? 0;
  if (a <= 0 && b <= 0) return null;
  if (p.stock <= a) return 'rojo';
  if (p.stock <= b) return 'ambar';
  return 'verde';
}
/** Cuánto comprar para llegar al tope (null si no hay tope o ya se alcanza). */
export function porComprar(p: { stock?: number | null; stockMax?: number | null }): number | null {
  if (p.stock == null || p.stockMax == null || p.stockMax <= p.stock) return null;
  return p.stockMax - p.stock;
}
export const NIVEL = {
  rojo: { txt: 'Rojo', punto: 'bg-red-500', texto: 'text-red-600 dark:text-red-400' },
  ambar: { txt: 'Ámbar', punto: 'bg-amber-400', texto: 'text-amber-600 dark:text-amber-400' },
  verde: { txt: 'Verde', punto: 'bg-emerald-500', texto: 'text-emerald-600 dark:text-emerald-400' },
} as const;

export function getProducts(all = false) {
  return api.get<{ products: Product[] }>(`/products${all ? '?all=1' : ''}`);
}
export function createProduct(p: { name: string; brand?: string; category?: string; price: number; cost?: number; sku?: string; stock?: number; notes?: string; description?: string }) {
  return api.post<{ id: string }>('/products', p);
}
/** PATCH parcial. `null` en un campo opcional lo VACÍA (undefined no viaja en el JSON). */
export function updateProduct(id: string, patch: { [K in keyof Omit<Product, 'id' | 'photoUrl'>]?: Product[K] | null }) {
  return api.put<{ ok: true }>(`/products/${id}`, patch);
}
export function deleteProduct(id: string) {
  return api.del<{ ok: true }>(`/products/${id}`);
}
/** Sube/reemplaza la foto (dataURL webp/jpg/png, máx 1.5MB tras el resize del front). */
export function setProductPhoto(id: string, image: string) {
  return api.post<{ ok: true; photoUrl: string }>(`/products/${id}/photo`, { image });
}
export function deleteProductPhoto(id: string) {
  return api.del<{ ok: true }>(`/products/${id}/photo`);
}
/** Renombra una categoría en todos sus productos. Si `to` ya existe, las dos se funden. */
export function renameCategory(from: string, to: string) {
  return api.post<{ ok: true; changed: number }>('/products/categories/rename', { from, to });
}
